import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import step1 from "../../../img/home-krok1-formularz.webp";
import step2 from "../../../img/home-krok2-szablon.webp";
import step3 from "../../../img/home-krok3-edycja.webp";
import step4 from "../../../img/home-krok4-pobierz.webp";

// „Jak to działa”: kroki po lewej, duży zrzut aktualnej aplikacji po prawej.
// Kroki przełączają się same, dopóki użytkownik nie kliknie albo nie najedzie na sekcję.

const AUTOPLAY_MS = 6000;

const STEPS = [
  {
    title: "Wpisz dane zmarłego",
    text: "Jeden formularz: dane osobowe, msza, pogrzeb i rodzina. Miejsca, cytaty i określenia wybierasz z własnej listy.",
    image: step1,
    alt: "Formularz zmarłego w Nekrodruku z wpisanymi danymi",
  },
  {
    title: "Wybierz materiał i szablon",
    text: "Nekrolog, klepsydra albo tabliczka – w ramkach Nekrodruku lub szablonach Twojego zakładu. Podgląd widać od razu.",
    image: step2,
    alt: "Wybór rodzaju materiału i ramki z podglądem nekrologu",
  },
  {
    title: "Dopracuj wygląd",
    text: "Popraw tekst albo przesuń elementy na podglądzie – zmiany widzisz na żywo, zanim cokolwiek wydrukujesz.",
    image: step3,
    alt: "Edycja tekstu nekrologu z podglądem na żywo",
  },
  {
    title: "Pobierz i drukuj",
    text: "Gotowy PDF lub JPG. Z tych samych danych zrobisz też tabliczkę na krzyż – bez ponownego wpisywania.",
    image: step4,
    alt: "Tabliczka na krzyż z tymi samymi danymi i przyciski pobierania PDF i JPG",
  },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function HowItWorks() {
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isStopped, setIsStopped] = useState(prefersReducedMotion);
  const tabRefs = useRef([]);

  useEffect(() => {
    if (isPaused || isStopped) return undefined;
    const timeoutId = setTimeout(() => setActive((current) => (current + 1) % STEPS.length), AUTOPLAY_MS);
    return () => clearTimeout(timeoutId);
  }, [active, isPaused, isStopped]);

  const selectStep = (index) => {
    setActive(index);
    setIsStopped(true);
  };

  // Strzałki przełączają kroki jak w zakładkach
  const handleKeyDown = (event, index) => {
    const delta = event.key === "ArrowDown" || event.key === "ArrowRight" ? 1
      : event.key === "ArrowUp" || event.key === "ArrowLeft" ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (index + delta + STEPS.length) % STEPS.length;
    selectStep(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <div
      className="home_howto"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <ol className="home_howtoSteps" role="tablist" aria-label="Kroki pracy w Nekrodruku">
        {STEPS.map((step, index) => {
          const isActive = index === active;
          return (
            <li key={step.title}>
              <button
                ref={(el) => (tabRefs.current[index] = el)}
                type="button"
                role="tab"
                id={`home-howto-tab-${index}`}
                aria-selected={isActive}
                aria-controls="home-howto-panel"
                tabIndex={isActive ? 0 : -1}
                className={`home_howtoStep${isActive ? " is-active" : ""}`}
                onClick={() => selectStep(index)}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                <span className="home_howtoNumber" aria-hidden="true">{index + 1}</span>
                <span className="home_howtoText">
                  <strong>{step.title}</strong>
                  <span>{step.text}</span>
                </span>
                {isActive && !isStopped && (
                  <span
                    key={active}
                    className={`home_howtoProgress${isPaused ? " is-paused" : ""}`}
                    style={{ animationDuration: `${AUTOPLAY_MS}ms` }}
                    aria-hidden="true"
                  />
                )}
              </button>
            </li>
          );
        })}
      </ol>

      <figure
        className="home_howtoScreen"
        id="home-howto-panel"
        role="tabpanel"
        aria-labelledby={`home-howto-tab-${active}`}
      >
        <div className="home_howtoBar" aria-hidden="true">
          <i />
          <i />
          <i />
          <span>nekrodruk.pl</span>
        </div>
        <div className="home_howtoImages">
          {STEPS.map((step, index) => (
            <img
              key={step.title}
              src={step.image}
              alt={index === active ? step.alt : ""}
              aria-hidden={index !== active}
              className={index === active ? "is-active" : ""}
              width="1200"
              height="800"
              loading="lazy"
            />
          ))}
        </div>
        <figcaption>
          <span>Krok {active + 1} z {STEPS.length}</span>
          <RouterLink to="/demo">Wypróbuj w demo →</RouterLink>
        </figcaption>
      </figure>
    </div>
  );
}
